import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Stack,
  Avatar,
  Container,
  IconButton
} from '@mui/material';
import UserAvatar from '../../components/Avatar/UserAvatar';
import {
  SmartToy as AIIcon,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';
import MarkdownRenderer from '../../components/Common/MarkdownRenderer';
import StudentLayout from './StudentLayout';

function MemoryDetailPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setError(null);
        // First get all conversations to find the one with matching ID
        const response = await axios.get('/notebook-examples/chat/conversations');
        const conv = response.data.find(c => c.id.toString() === conversationId);
        
        if (!conv) {
          setError('对话记录未找到');
          return;
        }

        // Get full conversation details
        const detailResponse = await axios.get(`/notebook-examples/chat/conversations/${conv.questionId}`);
        setConversation({
          ...conv,
          messages: detailResponse.data.messages || []
        });
      } catch (error) {
        console.error('Error fetching conversation:', error);
        setError(error.response?.data?.error || error.message || '获取对话记录失败');
      } finally {
        setLoading(false);
      }
    };

    if (conversationId) {
      fetchConversation();
    }
  }, [conversationId]);

  const handleGoToQuestion = (questionId) => {
    if (questionId.startsWith('notebook_')) {
      const exampleId = questionId.replace('notebook_', '');
      navigate(`/notebook-exercise/${exampleId}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      'proof': '证明题',
      'short_answer': '简答题',
      'multiple_choice': '选择题',
      'code': '代码题',
      'code_tracing': '代码追踪题'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <StudentLayout appBarTitle="对话详情">
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      </StudentLayout>
    );
  }

  if (error) {
    return (
      <StudentLayout appBarTitle="对话详情">
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography color="error" sx={{ mb: 2 }}>
              加载失败: {error}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              请检查网络连接或稍后重试
            </Typography>
          </Box>
        </Container>
      </StudentLayout>
    );
  }

  if (!conversation) {
    return (
      <StudentLayout appBarTitle="对话详情">
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography color="text.secondary">
              对话记录未找到
            </Typography>
          </Box>
        </Container>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout appBarTitle="对话详情">
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/memory')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {conversation.questionTitle || '未命名题目'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={getQuestionTypeLabel(conversation.questionType)}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                {formatDate(conversation.lastMessageAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {conversation.messageCount} 条消息
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleGoToQuestion(conversation.questionId)}
                sx={{ ml: 'auto' }}
              >
                <ArrowForward />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            {conversation.messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Card
                  sx={{
                    maxWidth: '80%',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary'
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      {msg.role === 'assistant' && (
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <AIIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, mb: 0.5, display: 'block' }}>
                          {msg.role === 'user' ? '你' : 'AI助手'}
                        </Typography>
                        <MarkdownRenderer content={msg.content} />
                      </Box>
                      {msg.role === 'user' && (
                        <UserAvatar 
                          user={user}
                          size={32}
                          sx={{ bgcolor: 'primary.contrastText', color: 'primary.main' }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Container>
    </StudentLayout>
  );
}

export default MemoryDetailPage;
